import React, { useState } from "react";
import { Modal, Button, Alert, Form, Badge, Card } from "react-bootstrap";
import Icon from "../icons/index";

const RefundModal = ({ show, onHide, onConfirm, orderData, isRefunding }) => {
	const [confirmText, setConfirmText] = useState("");
	const [isValid, setIsValid] = useState(false);

	const handleConfirmTextChange = (e) => {
		const value = e.target.value;
		setConfirmText(value);
		setIsValid(value.toLowerCase() === "refund");
	};

	const handleConfirm = () => {
		if (isValid) {
			onConfirm();
		}
	};

	const handleClose = () => {
		setConfirmText("");
		setIsValid(false);
		onHide();
	};

	// Validation logic based on order data
	const validateRefund = () => {
		if (!orderData) return { canRefund: false, alerts: [], errors: [] };

		const alerts = [];
		const errors = [];
		let canRefund = true;

		const status = orderData.status?.value;
		const paymentStatus = orderData.payment_status?.value;
		const giftType = orderData.gift_type;
		const isPartnerOrder = orderData.is_partner_order === 1;
		const partnerOrderId = orderData.partner_order_id;

		// Check gift type - Only these types can be refunded
		const allowedGiftTypes = ["OPAGIFT", "ASAP_PICK_UP", "SCHEDULED_PICK_UP"];
		if (!allowedGiftTypes.includes(giftType)) {
			errors.push("This order cannot be refunded due to its gift type.");
			canRefund = false;
		}

		// Check payment status
		if (paymentStatus === "PENDING" || paymentStatus === "FAILED") {
			errors.push("No payment was done to initiate refund.");
			canRefund = false;
		} else if (paymentStatus === "REFUNDED") {
			errors.push("Payment already refunded.");
			canRefund = false;
		} else if (paymentStatus === "SUCCESSFUL") {
			alerts.push({
				type: "success",
				message: "Payment was successful and qualifies for refund.",
			});
		}

		// Check order status
		if (status === "ACTIVE" && !partnerOrderId) {
			alerts.push({
				type: "info",
				message:
					"This order is safe to refund and has not been sent to kitchen yet.",
			});
		}

		// Partner order warnings
		if (isPartnerOrder) {
			alerts.push({
				type: "warning",
				message:
					"This order is a partner order, please check other partner portals upon refund to confirm order cancellation.",
			});
		}

		if (partnerOrderId) {
			alerts.push({
				type: "danger",
				message:
					"The order has already been sent to kitchen. Are you sure you want to proceed?",
			});
		}

		// General refund warning
		alerts.push({
			type: "warning",
			message:
				"Refunding will not cancel any order sent to integrations. It will only refund amount from Stripe!",
		});

		return { canRefund, alerts, errors };
	};

	const validation = validateRefund();

	return (
		<Modal
			show={show}
			onHide={handleClose}
			size="lg"
			centered
			className="refund-modal"
			style={{
				"--bs-modal-border-radius": "12px",
			}}
		>
			<Modal.Header closeButton className="bg-danger text-white border-0">
				<Modal.Title className="d-flex align-items-center">
					<i
						className="fas fa-exclamation-triangle me-3"
						style={{ fontSize: "1.5rem" }}
					></i>
					<span>Refund Order Confirmation</span>
				</Modal.Title>
			</Modal.Header>
			<Modal.Body className="p-4">
				{/* Order Status Information */}
				{orderData && (
					<Card className="mb-3 border-0 bg-light">
						<Card.Body className="p-3">
							<div className="d-flex align-items-center mb-2">
								<i className="fas fa-info-circle me-2 text-primary"></i>
								<h6 className="mb-0 fw-bold text-primary">Order Information</h6>
							</div>
							<div className="row g-3">
								<div className="col-md-6">
									<div className="d-flex flex-column">
										<small className="text-muted fw-medium mb-1">
											Order ID
										</small>
										<span className="fw-bold text-dark">
											{orderData.gift_id}
										</span>
									</div>
								</div>
								<div className="col-md-6">
									<div className="d-flex flex-column">
										<small className="text-muted fw-medium mb-1">
											Gift Type
										</small>
										<span className="fw-bold text-dark">
											{orderData.gift_type}
										</span>
									</div>
								</div>
								<div className="col-md-6">
									<div className="d-flex flex-column">
										<small className="text-muted fw-medium mb-1">Status</small>
										<div>
											<Badge
												bg={
													orderData.status?.value === "ACTIVE"
														? "success"
														: orderData.status?.value === "REDEEMED"
														? "info"
														: orderData.status?.value === "CLAIMED"
														? "warning"
														: "secondary"
												}
												className="px-2 py-1"
												style={{
													fontSize: "0.75rem",
													letterSpacing: "0.5px",
													textTransform: "uppercase",
												}}
											>
												{orderData.status?.value}
											</Badge>
										</div>
									</div>
								</div>
								<div className="col-md-6">
									<div className="d-flex flex-column">
										<small className="text-muted fw-medium mb-1">
											Payment Status
										</small>
										<div>
											<Badge
												bg={
													orderData.payment_status?.value === "SUCCESSFUL"
														? "success"
														: orderData.payment_status?.value === "PENDING"
														? "warning"
														: orderData.payment_status?.value === "FAILED"
														? "danger"
														: "secondary"
												}
												className="px-2 py-1"
												style={{
													fontSize: "0.75rem",
													letterSpacing: "0.5px",
													textTransform: "uppercase",
												}}
											>
												{orderData.payment_status?.value}
											</Badge>
										</div>
									</div>
								</div>
								{orderData.partner_order_id && (
									<div className="col-12">
										<div className="d-flex flex-column">
											<small className="text-muted fw-medium mb-1">
												Partner Order ID
											</small>
											<span className="fw-bold text-dark">
												{orderData.partner_order_id}
											</span>
										</div>
									</div>
								)}
							</div>
						</Card.Body>
					</Card>
				)}

				{/* Validation Alerts */}
				{validation.alerts.length > 0 && (
					<div className="mt-3">
						{validation.alerts.map((alert, index) => (
							<Alert
								key={index}
								variant={alert.type}
								className="mb-2 border-0 rounded-3 shadow-sm"
								style={{
									backgroundColor:
										alert.type === "success"
											? "#d1f2eb"
											: alert.type === "warning"
											? "#fef9e7"
											: alert.type === "danger"
											? "#fadbd8"
											: alert.type === "info"
											? "#d6eaf8"
											: "#e8f5e8",
									padding: "12px",
								}}
							>
								<div className="d-flex align-items-start">
									<i
										className={`fas ${
											alert.type === "success"
												? "fa-check-circle text-success"
												: alert.type === "warning"
												? "fa-exclamation-triangle text-warning"
												: alert.type === "danger"
												? "fa-times-circle text-danger"
												: alert.type === "info"
												? "fa-info-circle text-info"
												: "fa-info-circle text-primary"
										} me-2 mt-1`}
									></i>
									<span className="flex-grow-1">{alert.message}</span>
								</div>
							</Alert>
						))}
					</div>
				)}

				{/* Validation Errors */}
				{validation.errors.length > 0 && (
					<div className="mt-3">
						{validation.errors.map((error, index) => (
							<Alert
								key={index}
								variant="danger"
								className="mb-2 border-0 rounded-3 shadow-sm"
								style={{
									backgroundColor: "#fadbd8",
									padding: "12px",
								}}
							>
								<div className="d-flex align-items-start">
									<i className="fas fa-times-circle text-danger me-2 mt-1"></i>
									<div className="flex-grow-1">
										<strong>Error:</strong> {error}
									</div>
								</div>
							</Alert>
						))}
					</div>
				)}

				{/* Confirmation Input */}
				{validation.canRefund && (
					<Card className="mt-3 border-2 border-primary">
						<Card.Body className="p-3">
							<div className="d-flex align-items-center mb-2">
								<i className="fas fa-keyboard me-2 text-primary"></i>
								<h6 className="mb-0 fw-bold text-primary">
									Confirmation Required
								</h6>
							</div>
							<p className="text-muted mb-3">
								To proceed with this refund, please type{" "}
								<strong className="text-danger">&quot;refund&quot;</strong> in
								the field below:
							</p>
							<Form.Group className="mb-0">
								<Form.Control
									type="text"
									placeholder="Type 'refund' to confirm"
									value={confirmText}
									onChange={handleConfirmTextChange}
									className={`${
										isValid
											? "is-valid border-success"
											: confirmText
											? "is-invalid border-danger"
											: "border-primary"
									}`}
									style={{
										padding: "12px 16px",
										borderWidth: "2px",
										borderRadius: "6px",
									}}
								/>
								{confirmText && !isValid && (
									<div className="d-flex align-items-center mt-2">
										<i className="fas fa-exclamation-circle text-danger me-2"></i>
										<small className="text-danger fw-medium">
											Please type &quot;refund&quot; exactly to confirm.
										</small>
									</div>
								)}
								{isValid && (
									<div className="d-flex align-items-center mt-2">
										<i className="fas fa-check-circle text-success me-2"></i>
										<small className="text-success fw-medium">
											Confirmation text verified. You can now proceed with the
											refund.
										</small>
									</div>
								)}
							</Form.Group>
						</Card.Body>
					</Card>
				)}
			</Modal.Body>
			<Modal.Footer className="bg-light border-0 px-4 py-3">
				<div className="d-flex gap-3 w-100 justify-content-end align-items-center">
					<Button
						variant="outline-secondary"
						onClick={handleClose}
						className="px-4 py-2 fw-medium"
						style={{
							minWidth: "120px",
							borderRadius: "6px",
						}}
					>
						<i className="fas fa-times me-2"></i>
						Close
					</Button>
					{validation.canRefund && (
						<Button
							variant="danger"
							onClick={handleConfirm}
							disabled={!isValid || isRefunding}
							className="px-4 py-2 fw-medium shadow-sm"
							style={{
								minWidth: "160px",
								borderRadius: "6px",
								background:
									isValid && !isRefunding
										? "linear-gradient(135deg, #dc3545 0%, #c82333 100%)"
										: undefined,
								border: "none",
							}}
						>
							{isRefunding ? (
								<>
									<span
										className="spinner-border spinner-border-sm me-2"
										role="status"
										aria-hidden="true"
									></span>
									Processing...
								</>
							) : (
								<>
									<i className="fas fa-money-bill-wave me-2"></i>
									Confirm Refund
								</>
							)}
						</Button>
					)}
				</div>
			</Modal.Footer>
		</Modal>
	);
};



const CompleteOrderModal = ({
	show,
	onHide,
	onConfirm,
	orderData,
	isCompleting,
}) => {
	const [confirmText, setConfirmText] = useState("");
	const [isValid, setIsValid] = useState(false);

	const handleConfirmTextChange = (e) => {
		const value = e.target.value;
		setConfirmText(value);
		setIsValid(value.toLowerCase() === "confirm");
	};

	const handleConfirm = () => {
		if (isValid) {
			onConfirm();
		}
	};

	const handleClose = () => {
		setConfirmText("");
		setIsValid(false);
		onHide();
	};

	// Determine what steps will be taken
	const getCompletionSteps = () => {
		if (!orderData) return null;

		const status = orderData.status?.value;

		if (status === "REDEEMED") {
			return {
				steps: [
					"Order will be marked as CLAIMED",
					"Order will be marked as COMPLETED",
				],
				currentStatus: "REDEEMED",
			};
		} else if (status === "CLAIMED") {
			return {
				steps: ["Order will be marked as COMPLETED"],
				currentStatus: "CLAIMED",
			};
		}

		return null;
	};

	const completionInfo = getCompletionSteps();

	return (
		<Modal
			show={show}
			onHide={handleClose}
			size="lg"
			centered
			className="complete-order-modal"
			style={{
				"--bs-modal-border-radius": "12px",
			}}
		>
			<Modal.Header closeButton className="bg-success text-white border-0">
				<Modal.Title className="d-flex align-items-center">
					<i
						className="fas fa-check-circle me-3"
						style={{ fontSize: "1.5rem" }}
					></i>
					<span>Complete Order Confirmation</span>
				</Modal.Title>
			</Modal.Header>
			<Modal.Body className="p-4">
				{/* Order Status Information */}
				{orderData && (
					<Card className="mb-3 border-0 bg-light">
						<Card.Body className="p-3">
							<div className="d-flex align-items-center mb-2">
								<i className="fas fa-info-circle me-2 text-primary"></i>
								<h6 className="mb-0 fw-bold text-primary">Order Information</h6>
							</div>
							<div className="row g-3">
								<div className="col-md-6">
									<div className="d-flex flex-column">
										<small className="text-muted fw-medium mb-1">
											Order ID
										</small>
										<span className="fw-bold text-dark">
											{orderData.gift_id}
										</span>
									</div>
								</div>
								<div className="col-md-6">
									<div className="d-flex flex-column">
										<small className="text-muted fw-medium mb-1">
											Gift Type
										</small>
										<span className="fw-bold text-dark">
											{orderData.gift_type}
										</span>
									</div>
								</div>
								<div className="col-md-6">
									<div className="d-flex flex-column">
										<small className="text-muted fw-medium mb-1">
											Current Status
										</small>
										<div>
											<Badge
												bg={
													orderData.status?.value === "REDEEMED"
														? "info"
														: orderData.status?.value === "CLAIMED"
														? "warning"
														: "secondary"
												}
												className="px-2 py-1"
												style={{
													fontSize: "0.75rem",
													letterSpacing: "0.5px",
													textTransform: "uppercase",
												}}
											>
												{orderData.status?.value}
											</Badge>
										</div>
									</div>
								</div>
								<div className="col-md-6">
									<div className="d-flex flex-column">
										<small className="text-muted fw-medium mb-1">
											Payment Status
										</small>
										<div>
											<Badge
												bg={
													orderData.payment_status?.value === "SUCCESSFUL"
														? "success"
														: orderData.payment_status?.value === "PENDING"
														? "warning"
														: orderData.payment_status?.value === "FAILED"
														? "danger"
														: "secondary"
												}
												className="px-2 py-1"
												style={{
													fontSize: "0.75rem",
													letterSpacing: "0.5px",
													textTransform: "uppercase",
												}}
											>
												{orderData.payment_status?.value}
											</Badge>
										</div>
									</div>
								</div>
							</div>
						</Card.Body>
					</Card>
				)}

				{/* Completion Steps */}
				{completionInfo && (
					<Card className="mb-3 border-0 bg-light">
						<Card.Body className="p-3">
							<div className="d-flex align-items-center mb-3">
								<i className="fas fa-tasks me-2 text-success"></i>
								<h6 className="mb-0 fw-bold text-success">
									Completion Process
								</h6>
							</div>
							<p className="mb-2">
								<strong>Current Status:</strong> {completionInfo.currentStatus}
							</p>
							<p className="mb-2">
								<strong>The following actions will be performed:</strong>
							</p>
							<ul className="mb-0">
								{completionInfo.steps.map((step, index) => (
									<li key={index} className="mb-1">
										<i className="fas fa-arrow-right text-success me-2"></i>
										{step}
									</li>
								))}
							</ul>
						</Card.Body>
					</Card>
				)}

				{/* Critical Warning */}
				<Alert
					variant="danger"
					className="mb-3 border-0 rounded-3 shadow-sm"
					style={{
						backgroundColor: "#fadbd8",
						padding: "16px",
					}}
				>
					<div className="d-flex align-items-start">
						<i
							className="fas fa-exclamation-triangle text-danger me-3 mt-1"
							style={{ fontSize: "1.2rem" }}
						></i>
						<div className="flex-grow-1">
							<h6 className="fw-bold text-danger mb-2">
								⚠️ IMPORTANT - READ CAREFULLY
							</h6>
							<p className="mb-2">
								<strong className="text-danger">
									Only a restaurant should complete a gift order.
								</strong>{" "}
								This action is provided only for scenarios where there is an
								issue preventing the restaurant from completing a gift through
								normal channels.
							</p>
							<p className="mb-0">
								<strong className="text-danger">
									This action performs STRIPE operations which involve money
									transactions on the backend.
								</strong>{" "}
								Completing this order will trigger financial processes. Use with
								extreme caution.
							</p>
						</div>
					</div>
				</Alert>

				{/* Confirmation Input */}
				<Card className="mt-3 border-2 border-success">
					<Card.Body className="p-3">
						<div className="d-flex align-items-center mb-2">
							<i className="fas fa-keyboard me-2 text-success"></i>
							<h6 className="mb-0 fw-bold text-success">
								Confirmation Required
							</h6>
						</div>
						<p className="text-muted mb-3">
							To proceed with completing this order, please type{" "}
							<strong className="text-success">&quot;confirm&quot;</strong> in
							the field below:
						</p>
						<Form.Group className="mb-0">
							<Form.Control
								type="text"
								placeholder="Type 'confirm' to proceed"
								value={confirmText}
								onChange={handleConfirmTextChange}
								className={`${
									isValid
										? "is-valid border-success"
										: confirmText
										? "is-invalid border-danger"
										: "border-success"
								}`}
								style={{
									padding: "12px 16px",
									borderWidth: "2px",
									borderRadius: "6px",
								}}
							/>
							{confirmText && !isValid && (
								<div className="d-flex align-items-center mt-2">
									<i className="fas fa-exclamation-circle text-danger me-2"></i>
									<small className="text-danger fw-medium">
										Please type &quot;confirm&quot; exactly to proceed.
									</small>
								</div>
							)}
							{isValid && (
								<div className="d-flex align-items-center mt-2">
									<i className="fas fa-check-circle text-success me-2"></i>
									<small className="text-success fw-medium">
										Confirmation text verified. You can now complete the order.
									</small>
								</div>
							)}
						</Form.Group>
					</Card.Body>
				</Card>
			</Modal.Body>
			<Modal.Footer className="bg-light border-0 px-4 py-3">
				<div className="d-flex gap-3 w-100 justify-content-end align-items-center">
					<Button
						variant="outline-secondary"
						onClick={handleClose}
						className="px-4 py-2 fw-medium"
						style={{
							minWidth: "120px",
							borderRadius: "6px",
						}}
					>
						<i className="fas fa-times me-2"></i>
						Cancel
					</Button>
					<Button
						variant="success"
						onClick={handleConfirm}
						disabled={!isValid || isCompleting}
						className="px-4 py-2 fw-medium shadow-sm"
						style={{
							minWidth: "160px",
							borderRadius: "6px",
							background:
								isValid && !isCompleting
									? "linear-gradient(135deg, #28a745 0%, #218838 100%)"
									: undefined,
							border: "none",
						}}
					>
						{isCompleting ? (
							<>
								<span
									className="spinner-border spinner-border-sm me-2"
									role="status"
									aria-hidden="true"
								></span>
								Processing...
							</>
						) : (
							<>
								<i className="fas fa-check-double me-2"></i>
								Complete Order
							</>
						)}
					</Button>
				</div>
			</Modal.Footer>
		</Modal>
	);
};

export { RefundModal, CompleteOrderModal };


